<template>
  <div class="tab-content">
    <div class="mb-3">
      <h2 class="required mb-2">Название</h2>
      <va-input
        v-model.trim="nameModel"
        class="mb-0"
        :error="showNameError"
        :error-messages="showNameError ? ['Обязательно для заполнения'] : []"
      />
      <div v-if="mode === 'edit' && formData.id != null" class="product-id-hint">
        ID: {{ formData.id }}
      </div>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Наличие товара</h2>
      <fieldset>
        <va-radio-button
          option="true"
          :value="isAvailableRadio"
          @input="setAvailable(true)"
          label="в наличии"
        />
        <va-radio-button
          option="false"
          :value="isAvailableRadio"
          @input="setAvailable(false)"
          label="нет в наличии"
        />
      </fieldset>
    </div>
    <div v-if="mode === 'edit'" class="mb-4">
      <va-checkbox
        :value="formData.is_hidden"
        @input="update('is_hidden', $event)"
        label="Скрыть из публичной части сайта"
      />
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Категории</h2>
      <div class="mb-3">
        <va-input
          :value="categoriesSearch"
          @input="$emit('update:categoriesSearch', $event)"
          placeholder="Поиск категорий..."
          clearable
        />
      </div>
      <fieldset>
        <div v-for="category in filteredCategories" :key="category.id">
          <va-checkbox
            v-model="formData.categories"
            :label="category.name"
            :value="category.id"
            :array-value="String(category.id)"
          />
        </div>
        <div v-if="filteredCategories.length === 0" class="text-muted mt-2">
          Категории не найдены
        </div>
      </fieldset>
      <div v-if="selectedCategoryChips.length" class="category-chips mt-3">
        <div
          v-for="chip in selectedCategoryChips"
          :key="chip.id"
          class="category-chip"
          :class="{ 'category-chip--main': isMainCategory(chip.id) }"
        >
          <button
            type="button"
            class="category-chip__label"
            :title="isMainCategory(chip.id) ? 'Главная категория' : 'Сделать главной'"
            @click="$emit('set-main-category', chip.id)"
          >
            {{ chip.name }}
          </button>
          <button
            type="button"
            class="category-chip__remove"
            title="Убрать категорию"
            @click="$emit('remove-category', chip.id)"
          >
            ×
          </button>
        </div>
      </div>
      <p v-if="selectedCategoryChips.length" class="category-chips-hint text-muted mt-2">
        Нажмите на название, чтобы сделать категорию главной. Крестик — убрать из выбранных.
      </p>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Адрес страницы товара</h2>
      <div class="product-url-editor">
        <span v-if="categoryUrlPrefix" class="product-url-editor__prefix">{{ categoryUrlPrefix }}/</span>
        <va-input
          :value="formData.slug"
          class="product-url-editor__slug mb-0"
          placeholder="slug"
          @input="$emit('update-slug', $event)"
        />
      </div>
      <a
        v-if="productPageUrl"
        :href="productPageUrl"
        target="_blank"
        rel="noopener noreferrer"
        class="product-url-preview"
      >
        {{ productPageUrl }}
      </a>
      <p class="text-muted product-url-hint">
        Префикс берётся из главной категории. Редактируется только slug (последний сегмент).
      </p>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Дополнительные товары</h2>
      <div class="mb-2">
        <va-input
          :value="relatedProductsSearch"
          @input="$emit('update:relatedProductsSearch', $event)"
          placeholder="Поиск товаров для добавления..."
          clearable
        />
      </div>
      <div v-if="relatedProductsSearchResults.length > 0" class="related-products-search-results mb-2">
        <div v-for="p in relatedProductsSearchResults" :key="p.id" class="related-products-search-item">
          <span>{{ p.name }}</span>
          <va-button size="small" :disabled="isRelatedProductAdded(p.id)" @click="$emit('add-related-product', p)">
            {{ isRelatedProductAdded(p.id) ? "Добавлен" : "Добавить" }}
          </va-button>
        </div>
      </div>
      <div v-if="relatedProductsRows.length" class="related-products-list mt-3">
        <div v-for="(row, idx) in relatedProductsRows" :key="row.id" class="related-products-list__row">
          <span class="related-products-list__name">{{ row.name }}</span>
          <va-button flat size="small" color="gray" icon="fa fa-trash" @click="$emit('remove-related-product', idx)" />
        </div>
      </div>
      <div v-if="relatedProductsRows.length === 0" class="text-muted mt-2">
        Добавьте дополнительные товары через поиск выше
      </div>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Сопутствующие категории</h2>
      <div class="mb-3">
        <va-input
          :value="relatedCategoriesSearch"
          @input="$emit('update:relatedCategoriesSearch', $event)"
          placeholder="Поиск категорий..."
          clearable
        />
      </div>
      <fieldset>
        <div v-for="cat in filteredRelatedCategories" :key="cat.id">
          <va-checkbox
            v-model="formData.related_categories"
            :label="cat.name"
            :value="String(cat.id)"
            :array-value="String(cat.id)"
          />
        </div>
        <div v-if="filteredRelatedCategories.length === 0" class="text-muted mt-2">
          Категории не найдены
        </div>
      </fieldset>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Флорист (автор)</h2>
      <va-select
        :value="floristId"
        @input="$emit('update:floristId', $event)"
        :options="floristsOptions"
        value-by="id"
        text-by="name"
        placeholder="Выберите флориста"
        searchable
        clearable
        clear-value=""
      />
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Размер карточки товара</h2>
      <fieldset>
        <va-radio-button
          option="normal"
          v-model="formData.card_type"
          label="Стандартная"
        />
        <va-radio-button option="big" v-model="formData.card_type" label="Большая" />
      </fieldset>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Товары-компоненты</h2>
      <div class="mb-2">
        <va-checkbox v-model="formData.show_components" label="Показывать состав из товаров" />
      </div>
      <div class="mb-2">
        <va-checkbox
          v-model="formData.is_price_auto_calculated"
          label="Автоматически рассчитывать цену комплекта"
        />
      </div>
      <bundle-components-editor
        :search-query="bundleProductsSearch"
        :search-results="bundleProductsSearchResults"
        :rows="bundleComponentRows"
        @update:search-query="$emit('update:bundleProductsSearch', $event)"
        @add-product="$emit('add-bundle-product', $event)"
        @remove-row="$emit('remove-bundle-product', $event)"
      />
    </div>
    <div v-if="usedInBundles && usedInBundles.length > 0" class="mb-4">
      <h2 class="mb-2">Входит в состав наборов</h2>
      <ul class="used-in-bundles-list">
        <li v-for="bundle in usedInBundles" :key="bundle.id">
          <router-link :to="`/admin/products/${bundle.slug}/edit`">
            {{ bundle.name }}
          </router-link>
          <span v-if="bundle.quantity != null || bundle.discount_percent != null" class="used-in-bundles-meta">
            (кол-во: {{ bundle.quantity ?? '—' }}, скидка: {{ bundle.discount_percent != null ? bundle.discount_percent + '%' : '—' }})
          </span>
        </li>
      </ul>
    </div>
    <div class="mb-4">
      <h2 class="mb-2">Индивидуальный заказ</h2>
      <div class="mb-3">
        <va-checkbox
          :value="formData.is_individual_order"
          @input="update('is_individual_order', $event)"
          label="Товар «индивидуальный заказ» (клиент выбирает цену с шагом)"
        />
      </div>
      <template v-if="formData.is_individual_order">
        <div class="mb-3">
          <h2 class="required">Минимальная цена (₽)</h2>
          <input
            :value="formData.price_min"
            type="number"
            min="0"
            step="100"
            placeholder="3500"
            @input="update('price_min', $event.target.value ? Number($event.target.value) : null)"
          />
        </div>
        <div class="mb-3">
          <h2 class="required">Шаг цены (₽)</h2>
          <input
            :value="formData.price_step"
            type="number"
            min="1"
            step="1"
            placeholder="100"
            @input="update('price_step', $event.target.value ? Number($event.target.value) : null)"
          />
        </div>
        <p class="text-muted mb-4">
          На клиенте в корзине вместо счётчика количества будет счётчик увеличения цены от минимума с заданным шагом.
        </p>
      </template>
    </div>
    <div class="mb-4">
      <div class="mb-3">
        <h2 :class="{ required: !formData.is_individual_order }">Текущая цена</h2>
      </div>
      <div class="mb-3">
        <input
          :value="priceCurrent"
          type="number"
          :disabled="formData.is_price_auto_calculated"
          :placeholder="formData.is_individual_order ? 'Минимум для отображения' : ''"
          @input="$emit('update:priceCurrent', $event.target.value)"
        />
      </div>
      <div class="mb-3">
        <h2>Старая цена (опционально)</h2>
      </div>
      <div>
        <input
          :value="priceOld"
          type="number"
          @input="$emit('update:priceOld', $event.target.value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import BundleComponentsEditor from "../BundleComponentsEditor.vue";

export default {
  name: "ProductFormTabBasics",
  components: {
    BundleComponentsEditor,
  },
  props: {
    formData: { type: Object, required: true },
    validation: { type: Object, default: null },
    mode: { type: String, default: "create" },
    errors: { type: Object, default: () => ({}) },
    categoriesSearch: { type: String, default: "" },
    relatedProductsSearch: { type: String, default: "" },
    relatedCategoriesSearch: { type: String, default: "" },
    bundleProductsSearch: { type: String, default: "" },
    filteredCategories: { type: Array, default: () => [] },
    selectedCategoryChips: { type: Array, default: () => [] },
    categoryUrlPrefix: { type: String, default: "" },
    productPageUrl: { type: String, default: "" },
    effectiveMainCategoryId: { default: null },
    relatedProductsSearchResults: { type: Array, default: () => [] },
    relatedProductsRows: { type: Array, default: () => [] },
    filteredRelatedCategories: { type: Array, default: () => [] },
    floristsOptions: { type: Array, default: () => [] },
    floristId: { default: null },
    bundleComponentRows: { type: Array, default: () => [] },
    bundleProductsSearchResults: { type: Array, default: () => [] },
    usedInBundles: { type: Array, default: () => [] },
    priceCurrent: { default: "" },
    priceOld: { default: "" },
  },
  computed: {
    nameModel: {
      get() {
        return this.validation?.formData?.name?.$model ?? this.formData.name;
      },
      set(v) {
        if (this.validation?.formData?.name?.$model !== undefined) {
          this.validation.formData.name.$model = v;
        } else {
          this.formData.name = v;
        }
      },
    },
    showNameError() {
      return (
        this.errors.name ||
        (this.validation?.formData?.name?.$error &&
          !this.validation?.formData?.name?.required)
      );
    },
    isAvailableRadio() {
      const v = this.formData.is_available;
      const bool =
        v !== undefined && v !== null
          ? Boolean(v === true || v === 1 || v === "1" || v === "true")
          : true;
      return String(bool);
    },
  },
  methods: {
    update(field, value) {
      this.$emit("update:formData", { ...this.formData, [field]: value });
    },
    setAvailable(val) {
      const bool = val === "true" || val === true;
      this.$emit("update:formData", { ...this.formData, is_available: bool });
    },
    isMainCategory(categoryId) {
      const mainId = this.effectiveMainCategoryId;
      if (mainId == null || mainId === "") return false;
      return String(mainId) === String(categoryId);
    },
    isRelatedProductAdded(productId) {
      return this.relatedProductsRows.some((r) => r.id === productId);
    },
  },
};
</script>

<style scoped>
.tab-content {
  grid-column: 1 / -1;
}
fieldset {
  max-height: 300px;
  overflow: auto;
}

.related-products-search-results {
  max-height: 200px;
  overflow: auto;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 4px;
}

.related-products-search-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.related-products-search-item:last-child {
  border-bottom: none;
}

.related-products-list {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.related-products-list__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.related-products-list__row:last-child {
  border-bottom: none;
}

.related-products-list__name {
  flex: 1;
  min-width: 0;
}

.used-in-bundles-list {
  margin: 0;
  padding-left: 1.25rem;
  list-style: disc;
}
.used-in-bundles-list li {
  margin-bottom: 0.25rem;
}
.used-in-bundles-list a {
  color: var(--va-primary);
}
.used-in-bundles-meta {
  color: #666;
  font-size: 0.9em;
  margin-left: 0.25rem;
}
.error-message {
  color: #ff4444;
  font-size: 0.875rem;
  margin-top: 4px;
}

.product-id-hint {
  margin-top: 6px;
  color: #666;
  font-size: 0.875rem;
}

.category-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.category-chip {
  display: inline-flex;
  align-items: stretch;
  max-width: 100%;
  background: #2c2c2c;
  color: #fff;
  border-radius: 4px;
  overflow: hidden;
}

.category-chip--main {
  background: #2e7d32;
}

.category-chip__label {
  display: inline-flex;
  align-items: center;
  border: 0;
  background: transparent;
  color: inherit;
  cursor: pointer;
  padding: 8px 12px;
  font: inherit;
  text-align: left;
  max-width: 280px;
}

.category-chip__remove {
  border: 0;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
  background: #c62828;
  color: #fff;
  cursor: pointer;
  padding: 0 12px;
  font-size: 1.25rem;
  line-height: 1;
}

.category-chip__remove:hover {
  background: #b71c1c;
}

.category-chips-hint {
  font-size: 0.85rem;
  margin: 0;
}

.product-url-preview {
  display: inline-block;
  margin-top: 8px;
  color: var(--va-primary);
  word-break: break-all;
}

.product-url-editor {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

.product-url-editor__prefix {
  color: #666;
  font-family: monospace;
  font-size: 0.95rem;
  white-space: nowrap;
}

.product-url-editor__slug {
  flex: 1;
  min-width: 180px;
}

.product-url-hint {
  font-size: 0.85rem;
  margin: 8px 0 0;
}
</style>
